import { CloudOffline, DocumentBlank, Locked, Meter as MeterIcon, WarningAlt, type CarbonIconType } from '@carbon/icons-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { copyText } from '@/shared/lib/clipboard';
import { cx } from '@/shared/lib/cx';
import { useOnlineStatus } from '@/shared/lib/hooks';
import { Button } from './Button';
import { Meter } from './Meter';
import s from './States.module.css';

export interface StateLayoutProps {
  icon?: CarbonIconType;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** Компактный вариант — внутри таблицы, карточки или поповера. */
  compact?: boolean;
  align?: 'start' | 'center';
  tone?: 'neutral' | 'error' | 'warning';
  /** Уровень заголовка в документе. */
  headingLevel?: 2 | 3;
  role?: 'status' | 'alert';
  className?: string;
}

/** Общая раскладка состояний экрана (ТЗ 14.3). */
export function StateLayout({ icon: Icon = DocumentBlank, title, description, actions, children, compact = false, align = 'center', tone = 'neutral', headingLevel = 2, role, className }: StateLayoutProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <div
      className={cx(s.state, align === 'center' && s.centered, compact && s.compact, tone === 'error' && s.toneError, tone === 'warning' && s.toneWarning, className)}
      role={role}
    >
      <span className={s.iconWrap} aria-hidden="true">
        <Icon size={compact ? 24 : 32} />
      </span>
      <div className={s.text}>
        <Heading className={s.title}>{title}</Heading>
        {description ? <div className={s.description}>{description}</div> : null}
      </div>
      {children}
      {actions ? <div className={s.actions}>{actions}</div> : null}
    </div>
  );
}

export interface EmptyStateProps extends Omit<StateLayoutProps, 'title' | 'tone' | 'role'> {
  title: ReactNode;
  /** Подсказка первого действия: что сделать, чтобы здесь появились данные. */
  description: ReactNode;
}

/** Пусто: объясняет, почему пусто, и подсказывает первое действие. */
export function EmptyState(props: EmptyStateProps) {
  return <StateLayout {...props} />;
}

export interface ErrorStateProps {
  /** Что случилось. */
  title?: ReactNode;
  /** Что делать. */
  description?: ReactNode;
  /** Код запроса для поддержки (без персональных данных). */
  requestId?: string | null;
  onRetry?: () => void;
  retrying?: boolean;
  onContactSupport?: () => void;
  compact?: boolean;
  align?: 'start' | 'center';
  headingLevel?: 2 | 3;
  children?: ReactNode;
}

/** Ошибка: что случилось, что сделать, повтор и обращение в поддержку с кодом запроса. */
export function ErrorState({ title, description, requestId, onRetry, retrying = false, onContactSupport, compact, align, headingLevel, children }: ErrorStateProps) {
  const { t } = useTranslation('ui');
  const [copied, setCopied] = useState(false);
  return (
    <StateLayout
      icon={WarningAlt}
      tone="error"
      role="alert"
      compact={compact}
      align={align}
      headingLevel={headingLevel}
      title={title ?? t('states.error.title')}
      description={description ?? t('states.error.description')}
      actions={
        onRetry || onContactSupport ? (
          <>
            {onRetry ? (
              <Button variant="primary" onClick={onRetry} loading={retrying}>
                {t('states.error.retry')}
              </Button>
            ) : null}
            {onContactSupport ? (
              <Button variant="ghost" onClick={onContactSupport}>
                {t('states.error.contactSupport')}
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {children}
      {requestId ? (
        <p className={s.meta}>
          <span>{t('states.error.requestId')}:</span>
          <span className={s.code}>{requestId}</span>
          <button
            type="button"
            className={cx(s.copy, 'touch-target')}
            onClick={async () => {
              setCopied(await copyText(requestId));
            }}
          >
            {copied ? t('copied') : t('copy')}
          </button>
        </p>
      ) : null}
    </StateLayout>
  );
}

export interface OfflineStateProps {
  onRetry?: () => void;
  compact?: boolean;
  headingLevel?: 2 | 3;
}

/** Нет сети: что проверить; при появлении сети повтор выполняется сам. */
export function OfflineState({ onRetry, compact, headingLevel }: OfflineStateProps) {
  const { t } = useTranslation('ui');
  const online = useOnlineStatus();
  const [wasOffline, setWasOffline] = useState(!online);
  useEffect(() => {
    if (!online) setWasOffline(true);
    else if (wasOffline && onRetry) {
      setWasOffline(false);
      onRetry();
    }
  }, [online, wasOffline, onRetry]);
  return (
    <StateLayout
      icon={CloudOffline}
      tone="warning"
      role="status"
      compact={compact}
      headingLevel={headingLevel}
      title={t('states.offline.title')}
      description={t('states.offline.description')}
      actions={
        onRetry ? (
          <Button variant="tertiary" onClick={onRetry}>
            {t('states.offline.retry')}
          </Button>
        ) : null
      }
    />
  );
}

export interface ForbiddenStateProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  compact?: boolean;
  headingLevel?: 2 | 3;
}

/** Нет прав: почему и к кому обратиться. Не раскрывает, существует ли объект чужой клиники. */
export function ForbiddenState({ title, description, actions, compact, headingLevel }: ForbiddenStateProps) {
  const { t } = useTranslation('ui');
  return (
    <StateLayout
      icon={Locked}
      compact={compact}
      headingLevel={headingLevel}
      title={title ?? t('states.forbidden.title')}
      description={description ?? t('states.forbidden.description')}
      actions={actions}
    />
  );
}

export interface LimitReachedStateProps {
  /** Что исчерпано: «Приёмы скрайба в этом месяце». */
  limitName: ReactNode;
  used: number;
  limit: number;
  /** Что происходит сейчас и что будет дальше. */
  description: ReactNode;
  /** Действие владельцу (выбрать тариф) или остальным (сообщить владельцу). */
  actions?: ReactNode;
  compact?: boolean;
  headingLevel?: 2 | 3;
}

/** Лимит исчерпан (ПОРТ-32, СКР-35): сколько использовано, что будет, что сделать. */
export function LimitReachedState({ limitName, used, limit, description, actions, compact, headingLevel }: LimitReachedStateProps) {
  const { t } = useTranslation('ui');
  return (
    <StateLayout
      icon={MeterIcon}
      tone="warning"
      compact={compact}
      headingLevel={headingLevel}
      title={t('states.limit.title')}
      description={description}
      actions={actions}
    >
      <Meter label={limitName} value={used} max={limit} valueText={t('states.limit.usage', { used, limit })} />
    </StateLayout>
  );
}
